import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {
  defineMessage,
  Trans,
  useI18n,
} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {collectEnumValues} from '../../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {BoxedInput} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {BoxedTextAreaElement} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedTextAreaElement';
import {DropdownBoxCoordinator} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dialog} from '../../../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogContent} from '../../../../../../libs/shared/ui/src/components/dialogs/dialogContent';
import {DialogFooter} from '../../../../../../libs/shared/ui/src/components/dialogs/dialogFooter';
import {DialogHeader} from '../../../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useSynopsisViewerContext} from './synopsisViewerContext';

/*
 * Constants.
 */

enum SynopsisFeedbackReasonsEnum {
  MISSING = 'missing',
  HALLUCINATION = 'hallucination',
  VAGUE = 'vague',
  REDUNDANT = 'redundant',
  UI = 'ui',
  OTHER = 'other',
}

const reasonLabels: Record<SynopsisFeedbackReasonsEnum, ReactNode> = {
  [SynopsisFeedbackReasonsEnum.MISSING]: (
    <Trans id="synopsis-feedback-missing">Key information missing</Trans>
  ),
  [SynopsisFeedbackReasonsEnum.HALLUCINATION]: (
    <Trans id="synopsis-feedback-hallucination">Contradicts source content</Trans>
  ),
  [SynopsisFeedbackReasonsEnum.VAGUE]: <Trans id="synopsis-feedback-vague">Too vague</Trans>,
  [SynopsisFeedbackReasonsEnum.REDUNDANT]: <Trans id="synopsis-feedback-redundant">Redundant</Trans>,
  [SynopsisFeedbackReasonsEnum.UI]: <Trans id="synopsis-feedback-ui">User interface is confusing</Trans>,
  [SynopsisFeedbackReasonsEnum.OTHER]: <Trans id="synopsis-feedback-other">Other (please explain)</Trans>,
};

/*
 * Styles.
 */

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyleBoxedTextAreaElement = styled(BoxedTextAreaElement)`
  overflow-y: auto;
`;

/*
 * Component.
 */

export const SynopsisFeedbackModal: FC = () => {
  const {isFeedbackOpen, onSubmitFeedback, onCloseFeedback} = useSynopsisViewerContext();
  const [selectedReason, setSelectedReason] = useState<SynopsisFeedbackReasonsEnum | undefined>(undefined);
  const [text, setText] = useState('');
  const {i18n} = useI18n();

  const onClickSubmit = () => {
    const feedbackReason = selectedReason ?? 'unspecified';
    const feedback = `[${feedbackReason}] ${text}`.trim();

    onSubmitFeedback(feedback);
    setSelectedReason(undefined);
    setText('');
  };

  if (!isFeedbackOpen) {
    return null;
  }

  return (
    <Dialog onRequestClose={onCloseFeedback}>
      <DialogHeader title={<Trans id="synopsis-feedback-title">Share feedback about this summary</Trans>} />
      <DialogContent>
        <DropdownBoxCoordinator
          value={
            selectedReason ? (
              reasonLabels[selectedReason]
            ) : (
              <StyledPlaceholderDiv>
                <Trans id="synopsis-feedback-reason-placeholder">Select issue</Trans>
              </StyledPlaceholderDiv>
            )
          }
          renderDropdown={(popoverTransferProps: PopoverTransferProps) => (
            <SimpleDropdown {...popoverTransferProps} width={380}>
              {collectEnumValues(SynopsisFeedbackReasonsEnum).map((reason) => (
                <SimpleDropdownItem key={reason} onSelect={() => setSelectedReason(reason)}>
                  {reasonLabels[reason]}
                </SimpleDropdownItem>
              ))}
            </SimpleDropdown>
          )}
        />
        <BoxedInput>
          {(childProps) => (
            <StyleBoxedTextAreaElement
              {...childProps}
              value={text}
              onChange={setText}
              shouldFocus
              placeholder={i18n._(
                defineMessage({
                  id: 'synopsis-feedback-placeholder',
                  message: 'Tell us more (optional)',
                }),
              )}
              size={VisualSizesEnum.MEDIUM}
              rows={15}
            />
          )}
        </BoxedInput>
      </DialogContent>
      <DialogFooter>
        <ButtonGroupSpacer />
        <ButtonGroup>
          <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onCloseFeedback}>
            <Trans id="synopsis-feedback-cancel">Cancel</Trans>
          </Button>
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onClickSubmit} isDisabled={!selectedReason}>
            <Trans id="synopsis-feedback-submit">Submit</Trans>
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </Dialog>
  );
};
